import Button from "Components/Button";
import { CreateManualOrderResponse } from "hooks/OrderManagement/useOrders";
import React from "react";
import { cn } from "utils";
import { formatCurrency } from "utils/formatNumber";
import dayjs from "dayjs";
import { useGetMeQuery } from "hooks/Auth/useGetMeQuery";

const logo = require("assets/logo.png");

const DashedSeparator = () => {
  return <div className="border-b border-dashed border-gray-300 my-5"></div>;
};

const Label = ({ title = "" }) => (
  <div className="text-xs text-[#6D7278] font-normal text-left">{title}</div>
);

type OrderReceiptProps = {
  order: CreateManualOrderResponse | null;
};
export const OrderReceipt = ({ order }: OrderReceiptProps) => {
  const { data } = useGetMeQuery();

  const user = data?.data;

  const printReceipt = () => {
    window.print();
  };

  if (!order || !user) return null;

  const date = dayjs(order.createdAt || dayjs());
  const formattedDate = date.format("ddd, MMM D, YYYY • h:mm:ss A");

  return (
    <div className="bg-gray-100 flex justify-center min-h-screen font-robotoMono print:visible">
      <div className="bg-white p-6 w-[350px]">
        {/* Header */}
        <div className="text-center">
          <img
            src={logo}
            alt="Betty Distribution"
            className="mx-auto w-20 h-auto mb-2"
          />
          <p className="text-sm text-gray-600">{formattedDate}</p>
        </div>

        {/* Order ID */}
        <div className="my-4 text-center relative">
          <h2 className="text-xs font-medium absolute px-2 -top-2 bg-white w-fit left-0 right-0 mx-auto">
            Order ID
          </h2>
          <div className="text-2xl font-medium py-4 border border-dashed rounded-lg">
            {order.orderNo}
          </div>
        </div>

        {/* Customer Details */}
        <div className="text-sm text-gray-700 space-y-2">
          <p className="flex justify-between">
            <Label title="Customer Name" />
            {order?.customerName}
          </p>
          {/* <p className="flex justify-between">
            <Label title="Customer ID" />
            {order.customerId}
          </p> */}
          <p className="flex justify-between">
            <Label title="Customer No" />
            {order.customerPhone}
          </p>
          <p className="flex justify-between">
            <Label title="Payment Method" />
            {order.paymentMethod}
          </p>
        </div>

        <DashedSeparator />

        {/* Items */}
        <div className="">
          <div className="text-sm space-y-5">
            {order.orderProducts?.map((item) => (
              <div
                className="grid grid-cols-[2fr_1fr] place-content-between"
                key={item.orderId}>
                <div>
                  <Label title={String(item.productName)} />
                  <Label title={"x" + String(item.unit)} />
                </div>
                <span className="text-right">
                  {item.totalAmount
                    ? formatCurrency(item.totalAmount / 100)
                    : null}
                </span>
              </div>
            ))}
          </div>
        </div>

        <DashedSeparator />

        {/* Summary */}
        <div className="text-sm space-y-2">
          <div className="flex justify-between">
            <Label title="Amount" />
            <span>{formatCurrency(order.totalProductsAmount / 100)}</span>
          </div>
          <div className="flex justify-between">
            <Label title="Discount" />
            <span>{order.discountAmount}</span>
          </div>
          <div className="flex justify-between">
            <Label title="Shipping" />
            <span>Free</span>
          </div>
          <div className="flex justify-between font-semibold">
            <Label title="Total" />
            <span>{formatCurrency(order.totalProductsAmount / 100)}</span>
          </div>
        </div>

        <DashedSeparator />

        {/* Footer */}
        <div className="text-center text-xs text-gray-600 mt-4 space-y-2">
          <p className="text-xs grid grid-cols-2">
            <Label title="Operator" />
            {user?.firstName} {user?.lastName}
          </p>
          <p className="text-xs grid grid-cols-2">
            <Label title="Branch" />
            <span className="text-right">
              {order.branch.name}, {order.branch?.address}, {order.branch.state}
            </span>
          </p>

          <p className="mt-6">
            Thank you for choosing us! We appreciate your trust and look forward
            to serving you again.
          </p>
        </div>

        {/* Print Button */}
        <Button
          label="Print Receipt"
          onClick={printReceipt}
          className={cn("mt-4 w-full print:hidden")}
        />
      </div>
    </div>
  );
};
